/** Provider-neutral LiveKit transport for TalkieMate voice calls. */

const CONNECTION_TIMEOUT_MS = 20000;
const AGENT_READY_TIMEOUT_MS = 20000;
const AGENT_STATE_ATTRIBUTE = 'lk.agent.state';
const STOP_ASSISTANT_RPC = 'talkiemate.stop_assistant';
const AVATAR_CONNECTED_RPC = 'talkiemate.avatar.connected';
const AVATAR_READY_RPC = 'talkiemate.avatar.ready';
const AVATAR_PAUSE_RPC = 'talkiemate.avatar.pause';
const AVATAR_RESUME_RPC = 'talkiemate.avatar.resume';
const AVATAR_CLIENT_ATTRIBUTE = 'talkiemate.live_avatar_client';
let liveKitSdkPromise = null;

function getLiveCallModal() {
    return typeof document !== 'undefined' && typeof document.getElementById === 'function'
        ? document.getElementById('growtypeChatVideoCallModal')
        : null;
}

function loadLiveKitSdk() {
    if (window.LivekitClient) return Promise.resolve(window.LivekitClient);
    if (liveKitSdkPromise) return liveKitSdkPromise;

    liveKitSdkPromise = new Promise((resolve, reject) => {
        const finish = () => {
            if (window.LivekitClient) resolve(window.LivekitClient);
            else reject(new Error('LiveKit SDK loaded without exposing its browser API'));
        };
        const fail = () => reject(new Error('LiveKit SDK could not be loaded'));
        const existing = document.querySelector('script[data-growtype-chat-livekit-sdk]');
        if (existing) {
            existing.addEventListener('load', finish, { once: true });
            existing.addEventListener('error', fail, { once: true });
            return;
        }

        const runtime = document.querySelector(
            'script[src*="/growtype-chat/public/scripts/growtype-chat.js"]',
        );
        if (!runtime || !runtime.src) {
            fail();
            return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.dataset.growtypeChatLivekitSdk = '1';
        script.src = new URL('../libs/livekit/livekit-client.umd.js', runtime.src).href;
        script.addEventListener('load', finish, { once: true });
        script.addEventListener('error', fail, { once: true });
        document.head.appendChild(script);
    });
    return liveKitSdkPromise;
}

export function supportsLiveKitVoice() {
    return typeof window.RTCPeerConnection === 'function' &&
        !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

export function createLiveKitState() {
    return {
        room: null,
        avatarRoom: null,
        sdk: null,
        connecting: false,
        active: false,
        muted: false,
        fallbackStarted: false,
        connectionTimeout: null,
        agentReadyTimeout: null,
        audioUnlockHandler: null,
        audioElements: new Set(),
        avatarAudioElements: new Set(),
        avatarPlaybackMode: 'direct',
        avatarReturnedAudioTrack: null,
        videoElements: new Set(),
        assistantAudioTrack: null,
        avatarAudioTrack: null,
        avatarAudioContext: null,
        avatarAudioDestination: null,
        avatarAssistantSource: null,
        avatarSilenceOscillator: null,
        avatarSilenceGain: null,
        avatarConnectingPromise: null,
        avatarCredentialsKey: '',
        avatarReady: false,
        liveAvatarRequested: false,
        avatarFrameTimer: null,
        agentIdentity: '',
        assistantTranscript: '',
        finalTranscriptIds: new Set(),
    };
}

function clearTimers(state) {
    if (state.connectionTimeout) clearTimeout(state.connectionTimeout);
    if (state.agentReadyTimeout) clearTimeout(state.agentReadyTimeout);
    state.connectionTimeout = null;
    state.agentReadyTimeout = null;
}

function clearAudioUnlock(state) {
    if (!state.audioUnlockHandler) return;
    document.removeEventListener('click', state.audioUnlockHandler, true);
    state.audioUnlockHandler = null;
}

function requestAudioUnlock(state, ctx) {
    if (!state.room || state.audioUnlockHandler) return;
    ctx.onAudioPlaybackBlocked();
    state.audioUnlockHandler = () => {
        if (!state.room) return clearAudioUnlock(state);
        Promise.all([state.room, state.avatarRoom].filter(Boolean).map((room) => room.startAudio())).then(() => {
            ctx.debugLog('LiveKit remote audio unlocked by user gesture');
            clearAudioUnlock(state);
            updateAudioPlayback(state);
        }).catch((error) => {
            ctx.debugLog('LiveKit remote audio unlock failed', {
                name: error && error.name,
                message: error && error.message,
            });
        });
    };
    document.addEventListener('click', state.audioUnlockHandler, true);
}

function isAgent(participant, sdk) {
    return !!participant && (
        (sdk && participant.kind === sdk.ParticipantKind.AGENT) ||
        participant.isAgent === true ||
        !!(participant.attributes && participant.attributes[AGENT_STATE_ATTRIBUTE])
    );
}

function rememberAgent(state, participant, sdk) {
    // Tavus also joins as an agent, but control RPCs belong to our voice worker.
    if (String(participant?.identity || '').startsWith('live-avatar-')) return false;
    if (!isAgent(participant, sdk)) return false;
    state.agentIdentity = String(participant.identity || '');
    return true;
}

export async function interruptLiveKitAssistant(state) {
    state.audioElements.forEach((element) => {
        element.autoplay = false;
        element.muted = true;
        if (typeof element.pause === 'function') element.pause();
    });
    if (
        !state.agentIdentity ||
        !state.room ||
        typeof state.room.localParticipant.performRpc !== 'function'
    ) return false;
    try {
        await state.room.localParticipant.performRpc({
            destinationIdentity: state.agentIdentity,
            method: STOP_ASSISTANT_RPC,
            payload: '',
            responseTimeout: 2000,
        });
        return true;
    } catch (_error) {
        // Local playback remains silent if the control channel is unavailable.
        return false;
    }
}

function applyAgentState(participant, sdk, ctx) {
    if (!isAgent(participant, sdk)) return false;
    const agentState = participant.attributes && participant.attributes[AGENT_STATE_ATTRIBUTE];
    if (!agentState) return false;
    ctx.debugLog('LiveKit agent state changed', { state: agentState });
    ctx.onAgentState(agentState);
    return true;
}

function applyAvatarState(state, participant, ctx) {
    if (!participant || !participant.attributes) return false;
    const status = participant.attributes['talkiemate.live_avatar_status'];
    if (!status) return false;
    const message = participant.attributes['talkiemate.live_avatar_message'] || '';
    if (status === 'unavailable' && ctx.onAvatarUnavailable) {
        state.liveAvatarRequested = false;
        stopClientAvatarRoom(state);
        ctx.onAvatarUnavailable(message);
    }
    if (status === 'paused') {
        stopClientAvatarRoom(state);
        getLiveCallModal()?.classList.remove('has-live-avatar');
        if (ctx.onAvatarPaused) ctx.onAvatarPaused();
    }
    if (status === 'ready' && ctx.onAvatarReady) ctx.onAvatarReady();
    const serializedCredentials = participant.attributes[AVATAR_CLIENT_ATTRIBUTE] || '';
    if ((status === 'connecting' || status === 'client_ready') && serializedCredentials) {
        connectClientAvatarRoom(state, serializedCredentials, ctx).catch((error) => {
            ctx.debugLog('Provider avatar room connection failed', {
                name: error && error.name,
                message: error && error.message,
            });
            console.error('Provider avatar room connection failed', error);
        });
    }
    return true;
}

function updateAudioPlayback(state, ctx) {
    state.audioElements.forEach((element) => {
        const providerAudio = state.avatarAudioElements.has(element);
        const useProviderAudio = state.avatarPlaybackMode === 'provider';
        const audible = !state.muted && (providerAudio
            ? useProviderAudio && state.avatarReady
            : !useProviderAudio);
        element.muted = !audible;
        element.autoplay = audible;
        // Muted provider video must keep decoding while we validate frames.
        if (!audible && element.tagName !== 'VIDEO') {
            if (typeof element.pause === 'function') element.pause();
        } else {
            const playback = element.play();
            if (playback && typeof playback.catch === 'function') {
                playback.catch(() => { if (ctx) requestAudioUnlock(state, ctx); });
            }
        }
    });
}

function attachRemoteAudio(state, track, ctx, providerAudio = false, mediaElement = null) {
    if (!track || !state.sdk || track.kind !== state.sdk.Track.Kind.Audio) return;
    const element = mediaElement ? track.attach(mediaElement) : track.attach();
    element.autoplay = !state.muted;
    element.playsInline = true;
    element.setAttribute('aria-hidden', 'true');
    if (!mediaElement) element.style.display = 'none';
    element.muted = state.muted;
    if (!mediaElement) document.body.appendChild(element);
    state.audioElements.add(element);
    if (providerAudio) state.avatarAudioElements.add(element);
    element.setAttribute('data-live-call-audio', providerAudio ? 'avatar' : 'voice');
    const suppressed = state.muted || (providerAudio
        ? !state.avatarReady
        : state.avatarPlaybackMode === 'provider');
    element.muted = suppressed;
    element.autoplay = !suppressed;
    if (suppressed) {
        if (mediaElement) {
            element.autoplay = true;
            element.play().catch(() => requestAudioUnlock(state, ctx));
            return;
        }
        if (typeof element.pause === 'function') element.pause();
        return;
    }
    const playPromise = element.play();
    if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((error) => {
            ctx.debugLog('LiveKit remote audio requires user activation', {
                name: error && error.name,
                message: error && error.message,
            });
            requestAudioUnlock(state, ctx);
        });
    }
}

function attachRemoteVideo(state, track, ctx, options = {}) {
    if (!track || !state.sdk || track.kind !== state.sdk.Track.Kind.Video) return;
    const container = document.querySelector('#growtypeChatVideoCallModal .video-wrapper');
    if (!container) return;
    const element = track.attach();
    element.autoplay = true;
    element.playsInline = true;
    element.muted = true;
    element.classList.add('modal-videocall-live-avatar');
    if (options.deferReady) element.style.opacity = '0';
    container.appendChild(element);
    state.videoElements.add(element);
    if (options.deferReady) return element;
    getLiveCallModal()?.classList.add('has-live-avatar');
    if (ctx.onAvatarReady) ctx.onAvatarReady();
    return element;
}

async function confirmAvatarConnected(state, ctx) {
    if (!state.agentIdentity || !state.room || typeof state.room.localParticipant.performRpc !== 'function') return false;
    try {
        await state.room.localParticipant.performRpc({
            destinationIdentity: state.agentIdentity,
            method: AVATAR_CONNECTED_RPC,
            payload: '',
            responseTimeout: 2000,
        });
        return true;
    } catch (error) {
        ctx.debugLog('Live Avatar connection confirmation failed', {
            name: error && error.name,
            message: error && error.message,
        });
        return false;
    }
}

async function confirmAvatarReady(state, ctx) {
    if (
        !state.agentIdentity ||
        !state.room ||
        typeof state.room.localParticipant.performRpc !== 'function'
    ) return false;
    try {
        await state.room.localParticipant.performRpc({
            destinationIdentity: state.agentIdentity,
            method: AVATAR_READY_RPC,
            payload: '',
            responseTimeout: 2000,
        });
        return true;
    } catch (error) {
        ctx.debugLog('Live Avatar ready confirmation failed', {
            name: error && error.name,
            message: error && error.message,
        });
        return false;
    }
}

function frameContainsRenderedAvatar(element) {
    if (!element || element.readyState < 2 || !element.videoWidth || !element.videoHeight) return false;
    const canvas = document.createElement('canvas');
    canvas.width = 24;
    canvas.height = 24;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(element, 0, 0, canvas.width, canvas.height);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    let min = 255;
    let max = 0;
    for (let index = 0; index < pixels.length; index += 4) {
        const luma = (pixels[index] + pixels[index + 1] + pixels[index + 2]) / 3;
        min = Math.min(min, luma);
        max = Math.max(max, luma);
    }
    return max - min >= 12 && min < 245;
}

function markClientAvatarReady(state, track, ctx) {
    const element = attachRemoteVideo(state, track, ctx, { deferReady: true });
    if (!element) return;
    if (state.avatarReturnedAudioTrack) {
        // One media element holds the provider's audio and video tracks so
        // browser WebRTC lip-sync uses their shared media clock.
        attachRemoteAudio(state, state.avatarReturnedAudioTrack, ctx, true, element);
    }
    confirmAvatarConnected(state, ctx);
    const checkFrame = () => {
        if ((!state.avatarRoom && !state.room) || !element.isConnected) return;
        try {
            const audioReady = state.avatarPlaybackMode !== 'provider' || state.avatarAudioElements.size > 0;
            if (audioReady && frameContainsRenderedAvatar(element)) {
                element.style.opacity = '';
                getLiveCallModal()?.classList.add('has-live-avatar');
                if (ctx.onAvatarReady) ctx.onAvatarReady();
                if (!state.avatarReady) {
                    state.avatarReady = true;
                    updateAudioPlayback(state, ctx);
                    confirmAvatarReady(state, ctx);
                }
                state.avatarFrameTimer = null;
                return;
            }
        } catch (error) {
            ctx.debugLog('Live Avatar frame validation failed', {
                name: error && error.name,
                message: error && error.message,
            });
        }
        state.avatarFrameTimer = setTimeout(checkFrame, 250);
    };
    checkFrame();
}

function attachRoomTrack(state, track, participant, ctx) {
    if (String(participant?.identity || '').startsWith('live-avatar-')) {
        state.avatarPlaybackMode = 'provider';
        updateAudioPlayback(state, ctx);
        if (track.kind === state.sdk.Track.Kind.Audio) {
            state.avatarReturnedAudioTrack = track;
            state.videoElements.forEach((element) => {
                if (element.classList.contains('modal-videocall-live-avatar')) {
                    attachRemoteAudio(state, track, ctx, true, element);
                }
            });
        } else if (track.kind === state.sdk.Track.Kind.Video) {
            markClientAvatarReady(state, track, ctx);
        }
        return;
    }
    attachRemoteAudio(state, track, ctx);
    attachRemoteVideo(state, track, ctx);
    publishAssistantAudioToAvatar(state, track, participant, ctx);
}

function prepareAvatarAudioBridge(state) {
    if (state.avatarAudioContext && state.avatarAudioDestination) return true;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return false;
    state.avatarAudioContext = new AudioContextClass();
    state.avatarAudioDestination = state.avatarAudioContext.createMediaStreamDestination();
    state.avatarSilenceOscillator = state.avatarAudioContext.createOscillator();
    state.avatarSilenceGain = state.avatarAudioContext.createGain();
    state.avatarSilenceGain.gain.value = 0;
    state.avatarSilenceOscillator.connect(state.avatarSilenceGain);
    state.avatarSilenceGain.connect(state.avatarAudioDestination);
    state.avatarSilenceOscillator.start();
    state.avatarAudioContext.resume().catch(() => {});
    return true;
}

async function publishAssistantAudioToAvatar(state, track, participant, ctx) {
    if (
        !track ||
        !state.sdk ||
        track.kind !== state.sdk.Track.Kind.Audio ||
        !isAgent(participant, state.sdk)
    ) return;
    state.assistantAudioTrack = track;
    if (
        !state.avatarRoom ||
        state.avatarRoom.state !== state.sdk.ConnectionState.Connected ||
        !state.avatarAudioDestination ||
        !track.mediaStreamTrack
    ) return;
    try {
        if (state.avatarAssistantSource) state.avatarAssistantSource.disconnect();
        const sourceStream = new MediaStream([track.mediaStreamTrack]);
        state.avatarAssistantSource = state.avatarAudioContext.createMediaStreamSource(sourceStream);
        state.avatarAssistantSource.connect(state.avatarAudioDestination);
    } catch (error) {
        ctx.debugLog('Assistant audio bridge to Live Avatar failed', {
            name: error && error.name,
            message: error && error.message,
        });
    }
}

function stopClientAvatarRoom(state) {
    if (state.avatarFrameTimer) clearTimeout(state.avatarFrameTimer);
    state.videoElements.forEach((element) => {
        if (element.classList.contains('modal-videocall-live-avatar')) {
            element.srcObject = null;
            element.remove();
            state.videoElements.delete(element);
        }
    });
    getLiveCallModal()?.classList.remove('has-live-avatar');
    state.avatarAudioElements.forEach((element) => {
        element.pause();
        element.srcObject = null;
        element.remove();
        state.audioElements.delete(element);
    });
    state.avatarAudioElements.clear();
    if (state.avatarAssistantSource) {
        try { state.avatarAssistantSource.disconnect(); } catch (_error) {}
    }
    if (state.avatarSilenceOscillator) {
        try { state.avatarSilenceOscillator.stop(); } catch (_error) {}
        try { state.avatarSilenceOscillator.disconnect(); } catch (_error) {}
    }
    if (state.avatarSilenceGain) {
        try { state.avatarSilenceGain.disconnect(); } catch (_error) {}
    }
    if (state.avatarAudioTrack) {
        try { state.avatarAudioTrack.stop(); } catch (_error) {}
    }
    if (state.avatarAudioContext) {
        const closePromise = state.avatarAudioContext.close();
        if (closePromise && typeof closePromise.catch === 'function') closePromise.catch(() => {});
    }
    if (state.avatarRoom) {
        const disconnectPromise = state.avatarRoom.disconnect(true);
        if (disconnectPromise && typeof disconnectPromise.catch === 'function') {
            disconnectPromise.catch(() => {});
        }
        state.avatarRoom.removeAllListeners();
    }
    state.avatarRoom = null;
    state.avatarAudioTrack = null;
    state.avatarAudioContext = null;
    state.avatarAudioDestination = null;
    state.avatarAssistantSource = null;
    state.avatarSilenceOscillator = null;
    state.avatarSilenceGain = null;
    state.avatarConnectingPromise = null;
    state.avatarCredentialsKey = '';
    state.avatarReady = false;
    state.avatarFrameTimer = null;
    state.avatarPlaybackMode = 'direct';
    state.avatarReturnedAudioTrack = null;
    if (state.active) updateAudioPlayback(state);
}

async function connectClientAvatarRoom(state, serializedCredentials, ctx) {
    if (state.avatarCredentialsKey === serializedCredentials) {
        return state.avatarConnectingPromise;
    }
    let credentials;
    try {
        credentials = JSON.parse(serializedCredentials);
    } catch (_error) {
        throw new Error('Live Avatar client credentials are invalid');
    }
    if (
        credentials.transport !== 'livekit' ||
        !credentials.url ||
        !credentials.token ||
        !state.sdk
    ) {
        throw new Error('Live Avatar client credentials are incomplete');
    }

    if (state.avatarRoom || state.avatarCredentialsKey) stopClientAvatarRoom(state);
    state.avatarPlaybackMode = credentials.playback_audio === 'provider' ? 'provider' : 'direct';
    updateAudioPlayback(state);
    state.avatarCredentialsKey = serializedCredentials;
    state.avatarConnectingPromise = (async () => {
        const room = new state.sdk.Room({
            adaptiveStream: true,
            dynacast: true,
            disconnectOnPageLeave: true,
            publishDefaults: {
                dtx: false,
                red: false,
            },
        });
        state.avatarRoom = room;
        room.on(state.sdk.RoomEvent.TrackSubscribed, (track) => {
            if (track.kind === state.sdk.Track.Kind.Audio && state.avatarPlaybackMode === 'provider') {
                state.avatarReturnedAudioTrack = track;
                state.videoElements.forEach((element) => {
                    if (element.classList.contains('modal-videocall-live-avatar')) {
                        attachRemoteAudio(state, track, ctx, true, element);
                    }
                });
            }
            if (track.kind === state.sdk.Track.Kind.Video) {
                markClientAvatarReady(state, track, ctx);
            }
        });
        room.on(state.sdk.RoomEvent.TrackUnsubscribed, (track) => {
            track.detach().forEach((element) => {
                state.avatarAudioElements.delete(element);
                state.audioElements.delete(element);
                state.videoElements.delete(element);
                element.remove();
            });
        });
        room.on(state.sdk.RoomEvent.Disconnected, () => {
            if (state.avatarRoom !== room || state.muted || !state.active) return;
            // Stop vendor billing before falling back to the direct voice feed.
            setLiveAvatarPaused(state, true).catch(() => {});
            state.liveAvatarRequested = false;
            stopClientAvatarRoom(state);
            if (ctx.onAvatarUnavailable) ctx.onAvatarUnavailable('Real-time avatar disconnected. Continuing with voice only.');
        });
        await room.connect(credentials.url, credentials.token, { autoSubscribe: true });
        ctx.debugLog('Provider avatar room connected; publishing persistent audio track');
        if (!prepareAvatarAudioBridge(state)) {
            throw new Error('Web Audio is unavailable for Live Avatar');
        }
        const persistentMediaTrack = state.avatarAudioDestination.stream.getAudioTracks()[0];
        state.avatarAudioTrack = new state.sdk.LocalAudioTrack(persistentMediaTrack);
        await state.avatarRoom.localParticipant.publishTrack(state.avatarAudioTrack, {
            name: 'tts-audio',
            source: state.sdk.Track.Source.Unknown,
            // Atlas requires one continuous audio track. DTX would suppress
            // silence frames and can prevent the initial avatar stream.
            dtx: false,
            red: false,
        });
        ctx.debugLog('Provider avatar persistent audio track published');
        // Connection and visual readiness are separate. Confirming the audio
        // bridge now lets the worker generate speech; the later frame check is
        // still the only signal that starts avatar billing.
        await confirmAvatarConnected(state, ctx);
        if (state.assistantAudioTrack) {
            await publishAssistantAudioToAvatar(state, state.assistantAudioTrack, {
                kind: state.sdk.ParticipantKind.AGENT,
            }, ctx);
        }
        return room;
    })();
    return state.avatarConnectingPromise;
}

function handleTranscription(state, segments, participant, ctx) {
    if (!Array.isArray(segments) || !segments.length) return;
    const text = segments.map((segment) => String(segment.text || '')).join('').trim();
    if (!text) return;
    const final = segments.every((segment) => segment.final === true);
    const segmentId = segments.map((segment) => segment.id || '').join(':') || text;
    const fromAgent = isAgent(participant, state.sdk);
    const finalKey = `${fromAgent ? 'assistant' : 'user'}:${segmentId}`;
    if (final && state.finalTranscriptIds.has(finalKey)) return;

    if (fromAgent) {
        state.assistantTranscript = text;
        ctx.onAssistantTranscript(text, final);
    } else {
        ctx.onUserTranscript(text, final);
    }

    if (final) state.finalTranscriptIds.add(finalKey);
}

function failToFallback(state, ctx, error) {
    if (state.fallbackStarted) return;
    state.fallbackStarted = true;
    ctx.debugLog('LiveKit unavailable; activating existing voice transport', {
        name: error && error.name,
        message: error && error.message,
    });
    console.error('LiveKit transport failed; activating voice fallback', error);
    stopLiveKitCall(state);
    ctx.onFallback(error);
}

export function requestLiveKitSession(state, ctx) {
    if (!supportsLiveKitVoice()) {
        failToFallback(state, ctx, new Error('LiveKit WebRTC is not supported'));
        return false;
    }
    if (state.active || state.connecting) return true;
    // AudioContext must be resumed from the Start Call click. Preparing it here
    // keeps Atlas's persistent silent track alive when credentials arrive later.
    state.liveAvatarRequested = Boolean(ctx.liveAvatarEnabled);
    if (state.liveAvatarRequested) prepareAvatarAudioBridge(state);
    state.connecting = true;
    state.fallbackStarted = false;
    ctx.onConnecting();
    state.connectionTimeout = setTimeout(() => {
        failToFallback(state, ctx, new Error('LiveKit session bootstrap timed out'));
    }, CONNECTION_TIMEOUT_MS);
    try {
        const request = ctx.sendSessionRequest();
        if (request && typeof request.catch === 'function') {
            request.catch((error) => failToFallback(state, ctx, error));
        }
    } catch (error) {
        failToFallback(state, ctx, error);
        return false;
    }
    return true;
}

export async function connectLiveKitSession(state, credentials, ctx) {
    if (!credentials || !credentials.url || !credentials.token) {
        failToFallback(state, ctx, new Error('LiveKit session credentials are incomplete'));
        return false;
    }

    try {
        const sdk = ctx.loadSdk ? await ctx.loadSdk() : await loadLiveKitSdk();
        state.sdk = sdk;
        const room = new sdk.Room({
            adaptiveStream: true,
            dynacast: true,
            disconnectOnPageLeave: true,
            audioCaptureDefaults: {
                autoGainControl: true,
                echoCancellation: true,
                noiseSuppression: true,
                channelCount: 1,
            },
            publishDefaults: {
                audioPreset: { maxBitrate: 48000 },
                dtx: true,
                red: true,
            },
        });
        state.room = room;

        room.on(sdk.RoomEvent.TrackSubscribed, (track, _publication, participant) => {
            attachRoomTrack(state, track, participant, ctx);
        });
        room.on(sdk.RoomEvent.TrackUnsubscribed, (track) => {
            track.detach().forEach((element) => {
                state.audioElements.delete(element);
                state.avatarAudioElements.delete(element);
                state.videoElements.delete(element);
                element.remove();
            });
            if (!state.videoElements.size) {
                getLiveCallModal()?.classList.remove('has-live-avatar');
            }
        });
        room.on(sdk.RoomEvent.TranscriptionReceived, (segments, participant) => {
            handleTranscription(state, segments, participant, ctx);
        });
        room.on(sdk.RoomEvent.ParticipantConnected, (participant) => {
            applyAvatarState(state, participant, ctx);
            if (rememberAgent(state, participant, sdk)) {
                if (state.agentReadyTimeout) clearTimeout(state.agentReadyTimeout);
                state.agentReadyTimeout = null;
                applyAgentState(participant, sdk, ctx);
                ctx.onAgentConnected(credentials.provider || 'unknown');
                if (state.avatarReady) confirmAvatarReady(state, ctx);
                if (state.muted) {
                    interruptLiveKitAssistant(state);
                    setLiveAvatarPaused(state, true).catch(() => {});
                }
            }
        });
        room.on(sdk.RoomEvent.ParticipantAttributesChanged, (_changed, participant) => {
            applyAgentState(participant, sdk, ctx);
            applyAvatarState(state, participant, ctx);
        });
        room.on(sdk.RoomEvent.Reconnecting, () => ctx.onReconnecting());
        room.on(sdk.RoomEvent.Reconnected, () => ctx.onReconnected());
        room.on(sdk.RoomEvent.AudioPlaybackStatusChanged, () => {
            if (!room.canPlaybackAudio) requestAudioUnlock(state, ctx);
            else clearAudioUnlock(state);
        });
        room.on(sdk.RoomEvent.Disconnected, (reason) => {
            const wasActive = state.active;
            state.active = false;
            state.connecting = false;
            ctx.onDisconnected(reason, wasActive);
        });

        await room.connect(credentials.url, credentials.token, { autoSubscribe: true });
        await room.localParticipant.setMicrophoneEnabled(!state.muted, {
            autoGainControl: true,
            echoCancellation: true,
            noiseSuppression: true,
            channelCount: 1,
        });
        if (!state.muted) await room.startAudio().catch(() => {});
        clearTimers(state);
        state.connecting = false;
        state.active = true;
        ctx.onConnected(credentials.provider || 'unknown');

        let agentPresent = false;
        room.remoteParticipants.forEach((participant) => {
            applyAvatarState(state, participant, ctx);
            if (rememberAgent(state, participant, sdk)) {
                agentPresent = true;
                applyAgentState(participant, sdk, ctx);
                ctx.onAgentConnected(credentials.provider || 'unknown');
                if (state.avatarReady) confirmAvatarReady(state, ctx);
                if (state.muted) {
                    interruptLiveKitAssistant(state);
                    setLiveAvatarPaused(state, true).catch(() => {});
                }
            }
        });
        if (!agentPresent) {
            state.agentReadyTimeout = setTimeout(() => {
                failToFallback(state, ctx, new Error('LiveKit voice agent did not join the room'));
            }, AGENT_READY_TIMEOUT_MS);
        }
        return true;
    } catch (error) {
        failToFallback(state, ctx, error);
        return false;
    }
}

async function setLiveAvatarPaused(state, paused) {
    if (
        !state.liveAvatarRequested ||
        !state.agentIdentity ||
        !state.room ||
        typeof state.room.localParticipant.performRpc !== 'function'
    ) return false;
    await state.room.localParticipant.performRpc({
        destinationIdentity: state.agentIdentity,
        method: paused ? AVATAR_PAUSE_RPC : AVATAR_RESUME_RPC,
        payload: '',
        responseTimeout: 30000,
    });
    return true;
}

export async function setLiveKitMuted(state, muted) {
    state.muted = Boolean(muted);
    if (!state.room || !state.sdk || state.room.state !== state.sdk.ConnectionState.Connected) return false;

    updateAudioPlayback(state);

    if (!state.muted) await state.room.startAudio().catch(() => {});

    if (state.liveAvatarRequested) {
        if (state.muted) stopClientAvatarRoom(state);
        try {
            await setLiveAvatarPaused(state, state.muted);
        } catch (_error) {
            // Voice mute/unmute must still succeed if avatar control is unavailable.
        }
    }

    const microphoneUpdate = state.room.localParticipant.setMicrophoneEnabled(!state.muted);
    if (state.muted) await interruptLiveKitAssistant(state);
    await microphoneUpdate;
    return true;
}

export function stopLiveKitCall(state, options = {}) {
    clearTimers(state);
    clearAudioUnlock(state);
    state.active = false;
    state.connecting = false;
    stopClientAvatarRoom(state);
    if (state.room) {
        const disconnectPromise = state.room.disconnect(true);
        if (disconnectPromise && typeof disconnectPromise.catch === 'function') {
            disconnectPromise.catch(() => {});
        }
        state.room.removeAllListeners();
    }
    state.audioElements.forEach((element) => element.remove());
    state.audioElements.clear();
    state.videoElements.forEach((element) => element.remove());
    state.videoElements.clear();
    getLiveCallModal()?.classList.remove('has-live-avatar');
    state.room = null;
    state.sdk = null;
    state.agentIdentity = '';
    state.assistantAudioTrack = null;
    state.assistantTranscript = '';
    state.finalTranscriptIds.clear();
    state.liveAvatarRequested = false;
    if (!options.preserveMuted) state.muted = false;
}
